import { Card } from '../../components/ui/card';
import { FileText, AlertTriangle, Clock, Calendar, Bell } from 'lucide-react';

export const DashboardTab = ({ dashboard }) => {
  if (!dashboard) return null;
  const metrics = [
    { label: 'Active Guidelines', value: dashboard.total_active, icon: FileText, color: '#3b82f6' },
    { label: 'Overdue', value: dashboard.overdue_count, icon: AlertTriangle, color: '#ef4444' },
    { label: 'Due in 30 Days', value: dashboard.due_soon_count, icon: Clock, color: '#f59e0b' },
    { label: 'Avg Days Overdue', value: dashboard.avg_days_overdue, icon: Calendar, color: '#8b5cf6' },
  ];

  return (
    <div className="flex-1 overflow-y-auto p-6 space-y-4">
      <div className="grid grid-cols-4 gap-3">
        {metrics.map(m => (
          <Card key={m.label} className="p-4 border-gray-200">
            <div className="flex items-center gap-2 mb-1">
              <m.icon size={14} style={{ color: m.color }} />
              <span className="text-[10px] text-gray-400 font-medium">{m.label}</span>
            </div>
            <p className="text-2xl font-bold text-gray-900">{m.value}</p>
          </Card>
        ))}
      </div>

      <div className="grid grid-cols-3 gap-3">
        <Card className="p-3 border-gray-200 flex items-center gap-3">
          <div className="w-8 h-8 rounded-lg bg-violet-50 flex items-center justify-center"><Bell size={14} className="text-violet-500" /></div>
          <div><p className="text-[10px] text-gray-400">Reminders Sent</p><p className="text-lg font-bold text-gray-900">{dashboard.total_reminders_sent}</p></div>
        </Card>
        <Card className="p-3 border-gray-200 flex items-center gap-3">
          <div className="w-8 h-8 rounded-lg bg-orange-50 flex items-center justify-center"><AlertTriangle size={14} className="text-orange-500" /></div>
          <div><p className="text-[10px] text-gray-400">Escalated</p><p className="text-lg font-bold text-gray-900">{dashboard.escalated_count}</p></div>
        </Card>
        <Card className="p-3 border-gray-200 flex items-center gap-3">
          <div className="w-8 h-8 rounded-lg bg-amber-50 flex items-center justify-center"><Clock size={14} className="text-amber-500" /></div>
          <div><p className="text-[10px] text-gray-400">Paused</p><p className="text-lg font-bold text-gray-900">{dashboard.paused_count}</p></div>
        </Card>
      </div>

      {dashboard.overdue?.length > 0 && (
        <Card className="p-4 border-red-200">
          <h3 className="text-xs font-bold text-red-700 mb-2 flex items-center gap-1"><AlertTriangle size={12} /> Overdue Documents</h3>
          <div className="space-y-1.5">
            {dashboard.overdue.map(d => (
              <div key={d.id} className="flex items-center gap-2 text-xs bg-red-50 rounded px-2.5 py-1.5">
                <span className="font-medium text-gray-800 flex-1">{d.carrier} — {d.title}</span>
                <span className="text-[10px] text-gray-500">{d.state}</span>
                <span className="text-[10px] font-bold text-red-600">{d.days_overdue}d overdue</span>
                {d.escalated && <span className="text-[8px] font-bold bg-orange-100 text-orange-600 px-1 py-0.5 rounded">ESCALATED</span>}
                {d.reminder_paused && <span className="text-[8px] font-bold bg-amber-100 text-amber-600 px-1 py-0.5 rounded">PAUSED</span>}
                <span className="text-[9px] text-gray-400">{d.reminder_count || 0} reminder(s)</span>
                {d.assigned_to_name && <span className="text-[10px] text-gray-400">{d.assigned_to_name}</span>}
              </div>
            ))}
          </div>
        </Card>
      )}

      <div className="grid grid-cols-2 gap-3">
        <Card className="p-4 border-gray-200">
          <h3 className="text-xs font-bold text-gray-700 mb-2">By Carrier</h3>
          <div className="space-y-1">
            {Object.entries(dashboard.by_carrier || {}).sort((a, b) => b[1] - a[1]).map(([carrier, count]) => (
              <div key={carrier} className="flex items-center justify-between text-xs">
                <span className="text-gray-600">{carrier}</span>
                <span className="font-bold text-gray-800">{count}</span>
              </div>
            ))}
          </div>
        </Card>
        <Card className="p-4 border-gray-200">
          <h3 className="text-xs font-bold text-gray-700 mb-2">By State</h3>
          <div className="space-y-1">
            {Object.entries(dashboard.by_state || {}).sort((a, b) => b[1] - a[1]).map(([state, count]) => (
              <div key={state} className="flex items-center justify-between text-xs">
                <span className="text-gray-600">{state}</span>
                <span className="font-bold text-gray-800">{count}</span>
              </div>
            ))}
          </div>
        </Card>
      </div>

      {dashboard.recent_audit?.length > 0 && (
        <Card className="p-4 border-gray-200">
          <h3 className="text-xs font-bold text-gray-700 mb-2">Recent Activity</h3>
          <div className="space-y-1.5">
            {dashboard.recent_audit.map(a => (
              <div key={a.id} className="flex items-center gap-2 text-xs border-b border-gray-50 pb-1.5 last:border-0">
                <span className={`text-[9px] font-bold px-1.5 py-0.5 rounded ${
                  a.action?.includes('created') ? 'bg-green-100 text-green-700' :
                  a.action?.includes('deleted') ? 'bg-red-100 text-red-700' :
                  a.action?.includes('reminder') || a.action?.includes('escalation') ? 'bg-violet-100 text-violet-700' :
                  'bg-blue-100 text-blue-700'
                }`}>{a.action?.replace(/_/g, ' ')}</span>
                <span className="text-gray-600 flex-1">{a.details?.carrier || a.details?.title || ''}</span>
                <span className="text-[10px] text-gray-400">{a.user_name}</span>
                <span className="text-[9px] text-gray-400">{new Date(a.created_at).toLocaleDateString()}</span>
              </div>
            ))}
          </div>
        </Card>
      )}
    </div>
  );
};
